export const MIN_ADDRESS_AUTOCOMPLETE_CHARS = 3;

export const ADDRESS_PRIMARY_TYPES = [
  "street_address",
  "route",
  "premise",
  "subpremise",
];

const PLACE_DETAILS_FIELDS = [
  "addressComponents",
  "formattedAddress",
  "location",
] as const;

export interface PlacePredictionOption {
  placeId: string;
  primaryText: string;
  secondaryText: string;
  description: string;
}

export type PlacePredictionsById = Map<
  string,
  google.maps.places.PlacePrediction
>;

export const createPlaceAutocompleteSessionToken = (
  places: google.maps.PlacesLibrary,
) => new places.AutocompleteSessionToken();

export const fetchAddressPredictions = async (
  places: google.maps.PlacesLibrary,
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
): Promise<{
  options: PlacePredictionOption[];
  predictionsByPlaceId: PlacePredictionsById;
}> => {
  const { suggestions } =
    await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedRegionCodes: ["us"],
      includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
      sessionToken,
    });

  const predictionsByPlaceId: PlacePredictionsById = new Map();
  const options: PlacePredictionOption[] = [];

  suggestions.forEach((suggestion) => {
    const prediction = suggestion.placePrediction;
    if (!prediction) return;

    predictionsByPlaceId.set(prediction.placeId, prediction);
    options.push({
      placeId: prediction.placeId,
      primaryText: prediction.mainText?.text || prediction.text.text,
      secondaryText: prediction.secondaryText?.text || "",
      description: prediction.text.text,
    });
  });

  return { options, predictionsByPlaceId };
};

export const fetchPlaceDetails = async (
  places: google.maps.PlacesLibrary,
  predictionsByPlaceId: PlacePredictionsById,
  placeId: string,
): Promise<google.maps.places.Place> => {
  const prediction = predictionsByPlaceId.get(placeId);
  const place = prediction
    ? prediction.toPlace()
    : new places.Place({ id: placeId });

  await place.fetchFields({ fields: [...PLACE_DETAILS_FIELDS] });
  return place;
};
